import type { ComponentType, LazyExoticComponent } from "react";
import { lazy } from "react";
import { isChunkLoadError } from "@/shared/utils/chunkLoadError.util";

/**
 * Lazy-loaded route wrapper that recovers from stale chunk hashes after a
 * deploy. When the browser has an old app version cached and tries to
 * navigate to a lazy route, the chunk URL referenced in the old bundle no
 * longer exists on the CDN — `import()` throws a TypeError. We catch it and
 * force a hard reload so the user picks up the new bundle.
 *
 * A 30s session cooldown breaks the infinite-loop case where reload itself
 * fails (CDN actually down, hard caching, etc.) and lets the error surface.
 */
const RELOAD_AT_KEY = "yeati:lazyChunkReloadedAt";
const RELOAD_COOLDOWN_MS = 30_000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> => lazy<T>(async () => {
  try {
    return await factory();
  }
  catch (err) {
    if (!isChunkLoadError(err)) throw err;

    const lastReload = Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
    if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
      throw err;
    }
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
    window.location.reload();
    return new Promise<{ default: T }>(() => {});
  }
});
